import React from 'react';
import type { NavPage } from '../utils/routes';
import { T } from '../designTokens';
import { useIsMobile } from '../hooks/useMediaQuery';
import { ClaimParams, ClaimResult } from '../hooks/useClaimScan';
import { useCreditInfo } from '../hooks/useCreditInfo';
import LazyClaimLanding from './LazyClaimLanding';
import Header from './Header';

// ---------------------------------------------------------------------------
// Anonymous claim handoff (issue #870) — the www→dashboard funnel's landing
// screen for a signed-out visitor whose claim identity (URL params or the
// cross-subdomain cookie fallback, via resolveClaimIdentity()) matches the
// active #/scan/:id route. Renders the same ClaimLanding pitch HeroSection
// shows when no scan route is active — the underlying "locked" report has
// nothing to show that the honest pitch doesn't already promise.
// ---------------------------------------------------------------------------

interface AnonymousClaimHandoffProps {
  scanRouteId: string;
  claimParams: ClaimParams;
  /**
   * Resolved separately from claimParams.domain: the cross-subdomain cookie
   * fallback carries no domain, so the caller backfills it from the loaded
   * scan result's URL once available (issue #870).
   */
  domain: string | null;
  /**
   * The masked ScanResult from the anonymous scan-status poll, once it has
   * landed for this exact route — the teaser document's open-evidence source.
   * The "Peek at the lite report" escape hatch this replaced (issue #870) was
   * retired by the teaser-report redesign: the evidence now renders inline.
   */
  maskedResult: unknown;
  isClaiming: boolean;
  claimResult: ClaimResult | null;
  authEnabled: boolean;
  onDismiss: () => void;
  navigateTo: (page: NavPage) => void;
  queueCount: number;
  creditInfo: ReturnType<typeof useCreditInfo>;
  onBillingClick: () => void;
}

export default function AnonymousClaimHandoff({
  scanRouteId,
  claimParams,
  domain,
  maskedResult,
  isClaiming,
  claimResult,
  authEnabled,
  onDismiss,
  navigateTo,
  queueCount,
  creditInfo,
  onBillingClick,
}: AnonymousClaimHandoffProps) {
  const isMobile = useIsMobile();

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: T.gray50, display: 'flex', flexDirection: 'column' }}>
      <Header
        currentPage="scanner"
        onNavigate={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditBalance={creditInfo.balance}
        creditTier={creditInfo.tier}
        creditLoading={creditInfo.loading}
        creditHistory={creditInfo.hasCreditHistory}
        onBillingClick={onBillingClick}
      />
      {/* The teaser document is full-bleed: it brings its own dark hero and
          light body, so no card wrapper here (design brief "The Teaser
          Report"). */}
      <main style={{ flex: 1 }}>
        <LazyClaimLanding
          scanId={scanRouteId}
          isClaiming={isClaiming}
          claimResult={claimResult}
          onDismiss={onDismiss}
          isMobile={isMobile}
          authEnabled={authEnabled}
          domain={domain}
          score={claimParams.score}
          grade={claimParams.grade}
          maskedResult={maskedResult}
        />
      </main>
    </div>
  );
}
